// src/pages/MantenedorUsuarios.js
import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'

const API_BASE = 'https://garibcasinos.com/api'

const ROLES = [
  { value: 'ADMIN', label: 'ADMIN (ve todo)' },
  { value: 'COCINA', label: 'COCINA (solo sus máquinas)' },
  { value: 'AUDITOR', label: 'AUDITOR (solo lectura)' },
]

function safeJsonParse(s, fallback) {
  try {
    return JSON.parse(s)
  } catch {
    return fallback
  }
}

export default function MantenedorUsuarios() {
  const [users, setUsers] = useState([])
  const [maquinas, setMaquinas] = useState([])

  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState(null)
  const [err, setErr] = useState(null)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [form, setForm] = useState({
    Id: 0,
    Usuario: '',
    Password: '',
    NombreUsuario: '',
    Correo: '',
    Rol: 'COCINA',
    Activo: 1,
  })
  const [selMaquinas, setSelMaquinas] = useState([])

  const auth = useMemo(() => safeJsonParse(localStorage.getItem('auth') || '{}', {}), [])
  const isAdmin = String(auth?.rol || '').toUpperCase() === 'ADMIN' || localStorage.getItem('usuario') === 'Admin'

  useEffect(() => {
    if (!isAdmin) {
      setErr('No tienes permisos para ver este módulo (solo ADMIN).')
      return
    }
    cargarTodo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function cargarTodo() {
    setErr(null)
    setMsg(null)
    setLoading(true)
    try {
      const [uRes, mRes] = await Promise.all([
        axios.get(`${API_BASE}/listUsers.php`),
        axios.get(`${API_BASE}/listMaquinas.php`),
      ])

      if (!uRes.data?.ok) throw new Error(uRes.data?.error || 'Error listUsers')
      if (!mRes.data?.ok) throw new Error(mRes.data?.error || 'Error listMaquinas')

      setUsers(uRes.data.users || [])
      setMaquinas(mRes.data.maquinas || [])
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setErr(null)
    setMsg(null)
    setForm({
      Id: 0,
      Usuario: '',
      Password: '',
      NombreUsuario: '',
      Correo: '',
      Rol: 'COCINA',
      Activo: 1,
    })
    setSelMaquinas([])
    setIsModalOpen(true)
  }

  async function openEdit(id) {
    setErr(null)
    setMsg(null)
    setLoading(true)
    try {
      const res = await axios.get(`${API_BASE}/getUser.php`, { params: { id } })
      if (!res.data?.ok) throw new Error(res.data?.error || 'Error getUser')

      const u = res.data.user
      setForm({
        Id: Number(u.Id),
        Usuario: u.Usuario || '',
        Password: '', // no se trae
        NombreUsuario: u.NombreUsuario || '',
        Correo: u.Correo || '',
        Rol: (u.Rol || 'COCINA').toUpperCase(),
        Activo: Number(u.Activo) === 1 ? 1 : 0,
      })
      setSelMaquinas(Array.isArray(res.data.maquinas) ? res.data.maquinas : [])
      setIsModalOpen(true)
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setLoading(false)
    }
  }

  function closeModal() {
    setIsModalOpen(false)
  }

  function toggleMaquina(id) {
    setSelMaquinas((prev) => {
      const exists = prev.includes(id)
      if (exists) return prev.filter((x) => x !== id)
      return [...prev, id]
    })
  }

  function validateForm() {
    if (!form.Usuario.trim()) return 'Usuario es obligatorio'
    if (!form.NombreUsuario.trim()) return 'NombreUsuario es obligatorio'
    if (!form.Correo.trim()) return 'Correo es obligatorio'
    if (form.Id === 0 && !form.Password.trim()) return 'Password es obligatorio al crear'
    return null
  }

  async function guardar() {
    const v = validateForm()
    if (v) {
      setErr(v)
      return
    }

    setErr(null)
    setMsg(null)
    setLoading(true)

    try {
      // 1) Guardar usuario
      const res = await axios.post(`${API_BASE}/saveUser.php`, form, {
        headers: { 'Content-Type': 'application/json' },
      })

      if (!res.data?.ok) throw new Error(res.data?.error || 'Error saveUser')

      const userId = form.Id > 0 ? form.Id : Number(res.data.Id || 0)
      if (!userId) throw new Error('No se pudo obtener Id del usuario')

      // 2) Guardar máquinas (solo si no es ADMIN)
      const rol = String(form.Rol || 'COCINA').toUpperCase()
      if (rol !== 'ADMIN') {
        const resM = await axios.post(
          `${API_BASE}/saveUserMaquinas.php`,
          { id_usuario: userId, maquinas: selMaquinas },
          { headers: { 'Content-Type': 'application/json' } }
        )
        if (!resM.data?.ok) throw new Error(resM.data?.error || 'Error saveUserMaquinas')
      } else {
        // si es ADMIN, dejamos sin máquinas asignadas (opcional)
        await axios.post(
          `${API_BASE}/saveUserMaquinas.php`,
          { id_usuario: userId, maquinas: [] },
          { headers: { 'Content-Type': 'application/json' } }
        )
      }

      setMsg(form.Id > 0 ? '✅ Usuario actualizado' : '✅ Usuario creado')
      setIsModalOpen(false)
      await cargarTodo()
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setLoading(false)
    }
  }

  const maquinasActivas = useMemo(() => maquinas.filter((m) => Number(m.activa) === 1), [maquinas])

  return (
    <section className="section">
      <div className="container">
        <div className="is-flex is-justify-content-space-between is-align-items-center mb-4">
          <div>
            <h1 className="title">👤 Mantenedor de Usuarios</h1>
            <p className="subtitle is-6 has-text-grey">
              Crea usuarios, roles y asigna máquinas (cocinas).
            </p>
          </div>

          <div className="buttons">
            <button className={`button ${loading ? 'is-loading' : ''}`} onClick={cargarTodo}>
              🔄 Recargar
            </button>
            <button className="button is-primary" onClick={openCreate}>
              ➕ Nuevo Usuario
            </button>
          </div>
        </div>

        {err && <div className="notification is-danger is-light">{err}</div>}
        {msg && <div className="notification is-success is-light">{msg}</div>}

        <div className="box">
          <table className="table is-fullwidth is-striped is-hoverable">
            <thead>
              <tr>
                <th>ID</th>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Rol</th>
                <th>Activo</th>
                <th style={{ width: 140 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.Id}>
                  <td>{u.Id}</td>
                  <td>{u.Usuario}</td>
                  <td>{u.NombreUsuario}</td>
                  <td>{u.Correo}</td>
                  <td>
                    <span className="tag is-info is-light">{u.Rol}</span>
                  </td>
                  <td>
                    {Number(u.Activo) === 1 ? (
                      <span className="tag is-success is-light">SI</span>
                    ) : (
                      <span className="tag is-danger is-light">NO</span>
                    )}
                  </td>
                  <td>
                    <button className="button is-small" onClick={() => openEdit(Number(u.Id))}>
                      ✏️ Editar
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} className="has-text-centered has-text-grey">
                    {loading ? 'Cargando...' : 'Sin usuarios'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* MODAL */}
        <div className={`modal ${isModalOpen ? 'is-active' : ''}`}>
          <div className="modal-background" onClick={closeModal} />
          <div className="modal-card" style={{ width: 760, maxWidth: '95vw' }}>
            <header className="modal-card-head">
              <p className="modal-card-title">
                {form.Id > 0 ? `Editar Usuario #${form.Id}` : 'Nuevo Usuario'}
              </p>
              <button className="delete" aria-label="close" onClick={closeModal} />
            </header>

            <section className="modal-card-body">
              <div className="columns is-multiline">
                <div className="column is-6">
                  <label className="label">Usuario</label>
                  <input
                    className="input"
                    value={form.Usuario}
                    onChange={(e) => setForm((p) => ({ ...p, Usuario: e.target.value }))}
                    placeholder="cocina1 / admin"
                  />
                </div>

                <div className="column is-6">
                  <label className="label">Correo</label>
                  <input
                    className="input"
                    value={form.Correo}
                    onChange={(e) => setForm((p) => ({ ...p, Correo: e.target.value }))}
                    placeholder="cocina@empresa.cl"
                  />
                </div>

                <div className="column is-6">
                  <label className="label">Nombre Usuario</label>
                  <input
                    className="input"
                    value={form.NombreUsuario}
                    onChange={(e) => setForm((p) => ({ ...p, NombreUsuario: e.target.value }))}
                    placeholder="Juan Pérez"
                  />
                </div>

                <div className="column is-6">
                  <label className="label">Rol</label>
                  <div className="select is-fullwidth">
                    <select
                      value={form.Rol}
                      onChange={(e) => setForm((p) => ({ ...p, Rol: e.target.value }))}
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="column is-6">
                  <label className="label">
                    Password {form.Id > 0 ? '(dejar vacío si no cambia)' : '(obligatorio)'}
                  </label>
                  <input
                    className="input"
                    type="password"
                    value={form.Password}
                    onChange={(e) => setForm((p) => ({ ...p, Password: e.target.value }))}
                    placeholder="••••••••"
                  />
                  <p className="help">
                    Se guarda como SHA-256 automáticamente en el backend.
                  </p>
                </div>

                <div className="column is-6">
                  <label className="label">Activo</label>
                  <div className="select is-fullwidth">
                    <select
                      value={form.Activo}
                      onChange={(e) => setForm((p) => ({ ...p, Activo: Number(e.target.value) }))}
                    >
                      <option value={1}>SI</option>
                      <option value={0}>NO</option>
                    </select>
                  </div>
                </div>

                {/* Máquinas */}
                {String(form.Rol).toUpperCase() !== 'ADMIN' && (
                  <div className="column is-12">
                    <label className="label">Máquinas asignadas (cocinas)</label>
                    <div className="box" style={{ maxHeight: 260, overflow: 'auto' }}>
                      {maquinasActivas.map((m) => {
                        const id = Number(m.id_maquina)
                        const checked = selMaquinas.includes(id)
                        return (
                          <label key={id} className="checkbox" style={{ display: 'block', padding: '6px 0' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleMaquina(id)}
                              style={{ marginRight: 8 }}
                            />
                            <b>#{id}</b> — {m.nombre}
                            {m.cocina ? <span className="tag is-light ml-2">{m.cocina}</span> : null}
                          </label>
                        )
                      })}
                      {maquinasActivas.length === 0 && (
                        <p className="has-text-grey">No hay máquinas activas.</p>
                      )}
                    </div>
                    <p className="help">
                      Para COCINA/AUDITOR, esto define qué “cocinas” puede ver.
                    </p>
                  </div>
                )}
              </div>
            </section>

            <footer className="modal-card-foot is-justify-content-space-between">
              <button className="button" onClick={closeModal} disabled={loading}>
                Cancelar
              </button>
              <button className={`button is-primary ${loading ? 'is-loading' : ''}`} onClick={guardar}>
                Guardar
              </button>
            </footer>
          </div>
        </div>
      </div>
    </section>
  )
}
